import { useEffect, useId, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FileText, ImagePlus, RefreshCw, X } from 'lucide-react';
import {
  AdminAssetResponse,
  GiftCardDraft as DraftSchema,
  GiftCardResponse as ResponseSchema,
  giftCardExpiry,
  giftCardToday,
  type AdminAsset,
  type GiftCardDraft,
  type GiftCardResponse,
} from '@diana/contracts';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { AdminApiError } from '../../lib/api-client.js';
import { Button, IconButton, useDianaUIPortal, useEditPanelViewport } from '../../ui/index.js';
import { ContextualMediaField } from '../media/ContextualMediaField.js';
import {
  GiftCardEmptyState,
  GiftCardPreview,
  GiftCardSummary,
  GiftPhotoPicker,
} from './GiftCardPresentation.js';
import './GiftCardSection.css';

export function GiftCardSection({
  shootId,
  shootRevision,
  packageTitle,
  onDirtyChange,
  onBusyChange,
}: {
  shootId: string;
  shootRevision: number;
  packageTitle: string | null;
  onDirtyChange?(dirty: boolean): void;
  onBusyChange?(busy: boolean): void;
}) {
  const fieldErrorId = useId();
  const api = useAdminApi();
  const portal = useDianaUIPortal();
  const base = `/api/v1/admin/sessions/${shootId}/gift-card`;
  const defaults = (): GiftCardDraft => ({
    recipientName: '',
    personalMessage: '',
    experience: packageTitle
      ? /photoshoot$/i.test(packageTitle)
        ? packageTitle
        : `${packageTitle} photoshoot`
      : 'Personal photoshoot',
    validFrom: giftCardToday(),
    validUntil: giftCardExpiry(giftCardToday()),
    photoVariant: 'Individual',
    photoAssetId: null,
  });
  const [data, setData] = useState<GiftCardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<GiftCardDraft>(defaults);
  const [busy, setBusy] = useState(false);
  const gate = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState<string[]>([]);
  const [expiredManual, setExpiryManual] = useState(false);
  const [photoEditor, setPhotoEditor] = useState(false);
  const photoEpoch = useRef(0);
  const currentPhotoEpoch = photoEpoch.current;
  const [assets, setAssets] = useState<AdminAsset[]>([]);
  const { panelRef, bodyRef } = useEditPanelViewport(open);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(base, ResponseSchema)
      .then((result) => {
        if (alive) {
          setData(result);
          setLoadError('');
        }
      })
      .catch(() => {
        if (alive) setLoadError('Could not load the gift card.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [base, api]);
  const reload = async () => {
    setLoading(true);
    try {
      const next = await api.get(base, ResponseSchema);
      setData(next);
      if (open && next.giftCard) setDraft(next.giftCard.fields);
      setLoadError('');
      setError('');
    } catch {
      setLoadError('Could not load the gift card. Try again.');
    } finally {
      setLoading(false);
    }
  };
  const initial = data?.giftCard?.fields ?? defaults();
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const needsLayoutUpdate = Boolean(
    data?.giftCard && data.pdfReady && data.giftCard.fields.personalMessage === undefined,
  );
  useEffect(() => {
    onDirtyChange?.(open && dirty);
    return () => onDirtyChange?.(false);
  }, [open, dirty, onDirtyChange]);
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  useEffect(() => {
    if (!open || (!dirty && !busy)) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', unload);
    return () => window.removeEventListener('beforeunload', unload);
  }, [open, dirty, busy]);
  const close = () => {
    if (busy) return;
    if (dirty && !window.confirm('Discard unsaved gift card changes?')) return;
    photoEpoch.current++;
    setPhotoEditor(false);
    setOpen(false);
  };
  const start = () => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const fields = data?.giftCard?.fields ?? defaults();
    setDraft(fields);
    setError('');
    setInvalid([]);
    photoEpoch.current++;
    setPhotoEditor(false);
    setExpiryManual(fields.validUntil !== giftCardExpiry(fields.validFrom));
    setOpen(true);
    if (fields.photoAssetId)
      void api
        .get(`/api/v1/admin/assets/${fields.photoAssetId}`, AdminAssetResponse)
        .then((value) => setAssets([value.asset]))
        .catch(() => setAssets([]));
  };
  const update = (patch: Partial<GiftCardDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const save = async () => {
    if (gate.current || photoEditor) return;
    const parsed = DraftSchema.safeParse(draft);
    if (!parsed.success) {
      setInvalid(parsed.error.issues.map((issue) => String(issue.path[0])));
      setError(
        !draft.recipientName.trim()
          ? 'Add the recipient’s name.'
          : 'Check the highlighted gift card details.',
      );
      return;
    }
    gate.current = true;
    setBusy(true);
    setError('');
    setInvalid([]);
    try {
      const next = await api.request(base, {
        method: 'PUT',
        body: {
          fields: parsed.data,
          expectedRevision: data?.giftCard?.revision ?? 0,
          expectedShootRevision: shootRevision,
        },
        schema: ResponseSchema,
      });
      setData(next);
      setDraft(next.giftCard!.fields);
      photoEpoch.current++;
      setPhotoEditor(false);
      setOpen(false);
    } catch (problem) {
      setError(
        problem instanceof AdminApiError && problem.status === 409
          ? 'The gift card or shoot changed. Reload the latest details before trying again.'
          : problem instanceof AdminApiError && problem.fields?.length
            ? problem.fields.map((field) => field.message).join(' ')
            : 'Could not save the gift card. Your details are kept. Try again.',
      );
    } finally {
      gate.current = false;
      setBusy(false);
    }
  };
  const pdf = `${base}/pdf?download=1`;
  const date = (value: string) =>
    new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(value + 'T12:00:00Z'));
  return (
    <section
      ref={sectionRef}
      className="crm-detail-section gift-card-section"
      aria-labelledby={`gift-card-${shootId}`}
    >
      <header>
        <h2 id={`gift-card-${shootId}`} className="crm-section-title">
          Gift card
        </h2>
      </header>
      {loading ? (
        <p className="gift-card-section__muted">Loading gift card…</p>
      ) : loadError ? (
        <p role="alert">
          {loadError}{' '}
          <button className="shoot-button shoot-button--outline" onClick={() => void reload()}>
            Try again
          </button>
        </p>
      ) : data?.giftCard && data.pdfReady ? (
        <GiftCardSummary
          recipient={data.giftCard.fields.recipientName}
          experience={data.giftCard.fields.experience}
          validUntil={date(data.giftCard.fields.validUntil)}
          photo={
            data.giftCard.fields.photoAssetId
              ? `/api/v1/admin/assets/${data.giftCard.fields.photoAssetId}/preview`
              : `${base}/photo/${data.giftCard.fields.photoVariant}`
          }
          onEdit={start}
          downloadUrl={data.pdfReady ? pdf : undefined}
        />
      ) : (
        <GiftCardEmptyState onGenerate={start} />
      )}
      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="shoot-panel__overlay" />
          <Dialog.Content
            ref={panelRef}
            className="shoot-panel gift-card-panel dui-edit-panel"
            data-edit-panel="true"
            data-mode="gift-card"
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              close();
            }}
            onPointerDownOutside={(event) => {
              event.preventDefault();
              close();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const target =
                sectionRef.current?.querySelector<HTMLElement>('.gift-card-document__edit') ??
                (opener.current?.isConnected ? opener.current : null);
              target?.focus({ preventScroll: true });
            }}
            onOpenAutoFocus={(event) => {
              if (window.matchMedia('(max-width: 1023px)').matches) {
                event.preventDefault();
                document
                  .querySelector<HTMLElement>('[aria-label="Close gift card"]')
                  ?.focus({ preventScroll: true });
              }
            }}
          >
            <header className="shoot-panel__header" data-panel-header>
              <Dialog.Title>Gift card</Dialog.Title>
              <IconButton
                variant="quiet"
                icon={X}
                label="Close gift card"
                onClick={close}
                disabled={busy}
              />
            </header>
            <Dialog.Description className="sr-only">
              Save the gift card settings and PDF together. This does not change the shoot status or
              payment.
            </Dialog.Description>
            <div ref={bodyRef} className="shoot-panel__body gift-card-panel__body" data-panel-body>
              <fieldset disabled={busy} className="gift-card-panel__fields">
                <div className="gift-card-panel__group">
                  <label className="field">
                    <span>Recipient</span>
                    <input
                      aria-label="Recipient"
                      value={draft.recipientName}
                      maxLength={80}
                      placeholder="e.g. Sofia"
                      disabled={busy}
                      aria-invalid={invalid.includes('recipientName')}
                      aria-describedby={
                        invalid.includes('recipientName') ? `${fieldErrorId}-recipient` : undefined
                      }
                      onChange={(event) => update({ recipientName: event.currentTarget.value })}
                    />
                    {invalid.includes('recipientName') ? (
                      <span
                        id={`${fieldErrorId}-recipient`}
                        className="gift-card-panel__field-error"
                      >
                        Add the recipient’s name, up to 80 characters.
                      </span>
                    ) : null}
                  </label>
                  <label className="field">
                    <span>Photoshoot</span>
                    <input
                      aria-label="Photoshoot"
                      value={draft.experience}
                      maxLength={140}
                      disabled={busy}
                      aria-invalid={invalid.includes('experience')}
                      aria-describedby={
                        invalid.includes('experience') ? `${fieldErrorId}-experience` : undefined
                      }
                      onChange={(event) => update({ experience: event.currentTarget.value })}
                    />
                    {invalid.includes('experience') ? (
                      <span
                        id={`${fieldErrorId}-experience`}
                        className="gift-card-panel__field-error"
                      >
                        Add a short photoshoot title, up to 140 characters.
                      </span>
                    ) : null}
                  </label>
                </div>
                <div className="gift-card-panel__group">
                  <label className="field">
                    <span>
                      Personal message <small>(optional)</small>
                    </span>
                    <textarea
                      aria-label="Personal message"
                      maxLength={200}
                      value={draft.personalMessage ?? ''}
                      disabled={busy}
                      placeholder="A little time just for you. Happy birthday, Sofia."
                      aria-invalid={invalid.includes('personalMessage')}
                      aria-describedby={
                        invalid.includes('personalMessage') ? `${fieldErrorId}-message` : undefined
                      }
                      onChange={(event) => update({ personalMessage: event.currentTarget.value })}
                    />
                    {invalid.includes('personalMessage') ? (
                      <span id={`${fieldErrorId}-message`} className="gift-card-panel__field-error">
                        Use up to 200 characters of plain text.
                      </span>
                    ) : null}
                  </label>
                </div>
                <div className="gift-card-panel__group">
                  <div className="gift-card-panel__dates">
                    <label className="field">
                      <span>Valid from</span>
                      <input
                        type="date"
                        aria-label="Valid from"
                        value={draft.validFrom}
                        disabled={busy}
                        aria-invalid={invalid.includes('validFrom')}
                        onChange={(event) => {
                          const validFrom = event.currentTarget.value;
                          let validUntil = draft.validUntil;
                          if (!expiredManual)
                            try {
                              validUntil = giftCardExpiry(validFrom);
                            } catch {}
                          update({ validFrom, validUntil });
                        }}
                      />
                    </label>
                    <label className="field">
                      <span>Valid until</span>
                      <input
                        type="date"
                        aria-label="Valid until"
                        value={draft.validUntil}
                        disabled={busy}
                        aria-invalid={invalid.includes('validUntil')}
                        onChange={(event) => {
                          setExpiryManual(true);
                          update({ validUntil: event.currentTarget.value });
                        }}
                      />
                    </label>
                  </div>
                  {invalid.some((field) => field === 'validFrom' || field === 'validUntil') ? (
                    <p className="gift-card-panel__field-error">
                      Choose valid dates, with the expiry after the start date.
                    </p>
                  ) : null}
                </div>
                <div className="gift-card-panel__group">
                  <GiftPhotoPicker
                    value={draft.photoVariant}
                    custom={Boolean(draft.photoAssetId)}
                    disabled={busy}
                    photoSrc={(variant) =>
                      `${base}/photo/${variant}?v=${data?.giftCard?.revision ?? 0}`
                    }
                    onChange={(variant) => {
                      photoEpoch.current++;
                      update({ photoVariant: variant, photoAssetId: null });
                      setPhotoEditor(false);
                    }}
                  />
                  <div className="gift-card-panel__photo-actions">
                    <button
                      type="button"
                      className="shoot-button shoot-button--outline"
                      disabled={busy}
                      onClick={() => {
                        photoEpoch.current++;
                        setPhotoEditor((value) => !value);
                      }}
                    >
                      <ImagePlus size={16} />
                      {photoEditor
                        ? 'Cancel photo change'
                        : draft.photoAssetId
                          ? 'Change custom photo'
                          : 'Choose another photo'}
                    </button>
                    {draft.photoAssetId ? (
                      <button
                        className="shoot-button shoot-button--outline"
                        disabled={busy}
                        onClick={() => {
                          photoEpoch.current++;
                          update({ photoAssetId: null });
                          setPhotoEditor(false);
                        }}
                      >
                        Use preset photo
                      </button>
                    ) : null}
                  </div>
                  {photoEditor ? (
                    <ContextualMediaField
                      assets={assets}
                      title="Gift photo"
                      profile="standard"
                      cropProfile="landscape-3-2"
                      value={draft.photoAssetId ? { assetId: draft.photoAssetId } : undefined}
                      onChange={(value) => {
                        if (gate.current || photoEpoch.current !== currentPhotoEpoch) return;
                        update({ photoAssetId: value.assetId });
                        photoEpoch.current++;
                        setPhotoEditor(false);
                      }}
                    />
                  ) : null}
                  {draft.photoAssetId ? (
                    <span className="gift-card-panel__custom-label">Custom photo selected</span>
                  ) : null}
                </div>
              </fieldset>
              <GiftCardPreview key={`${shootId}-${open}`} draft={draft} shootId={shootId} />
            </div>
            <footer className="shoot-panel__footer gift-card-panel__footer" data-panel-footer>
              {error ? (
                <div className="gift-card-panel__error" role="alert">
                  <p>{error}</p>
                  {error.includes('Reload') ? (
                    <button
                      className="shoot-button shoot-button--outline"
                      onClick={() => {
                        if (
                          !dirty ||
                          window.confirm('Discard your draft and reload the latest gift card?')
                        )
                          void reload();
                      }}
                      disabled={busy}
                    >
                      <RefreshCw size={16} />
                      Reload latest
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div>
                <button
                  className="shoot-button shoot-button--primary"
                  disabled={
                    busy || photoEditor || (!dirty && Boolean(data?.pdfReady) && !needsLayoutUpdate)
                  }
                  onClick={() => void save()}
                >
                  <FileText size={16} />
                  {busy ? 'Saving…' : 'Save card'}
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}
